'use client';
import { type PlayFunction } from '@/components/common/useContentHelper';
import { ProGloveWorkerFeedbackActionId, useProGlove } from '@/components/proglove/ProgloveProvider';
import { soundAtom } from '@/context/atoms/Sound';
import { useAtom } from 'jotai/index';
import { useSnackbar, type OptionsWithExtraProps, type VariantType } from 'notistack';
import { useCallback, useRef } from 'react';
import * as UseSound from 'use-sound';

export interface UserFeedback<V extends VariantType> {
  enqueueFeedback: (message: string, options?: OptionsWithExtraProps<V>, mute?: boolean) => void;
  playSuccessSound: PlayFunction;
  playErrorSound: PlayFunction;
  soundEnabled: boolean;
}

const variantWorkerFeedbackMap: Record<VariantType, ProGloveWorkerFeedbackActionId | null> = {
  success: ProGloveWorkerFeedbackActionId.FEEDBACK_POSITIVE,
  error: ProGloveWorkerFeedbackActionId.FEEDBACK_NEGATIVE,
  warning: ProGloveWorkerFeedbackActionId.FEEDBACK_NEGATIVE,
  info: null,
  default: null,
};

export interface UserFeedbackOptions {
  skipProGlove?: boolean;
  skipSound?: boolean;
}

export default function useUserFeedback(userFeedbackOptions?: UserFeedbackOptions): UserFeedback<VariantType> {
  const { skipProGlove = false, skipSound = false } = userFeedbackOptions ?? {};
  // @TODO useSingleSnackbar is not 100% tested, if there is any error related to it, just change this line to useSnackbar(); for a quick fix and further investigate the issue
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const { workerFeedback } = useProGlove();
  // @TODO use-sound won't work always this way, because it has to be loaded after an user-interaction. pls refactor, thx
  const [{ enabled: soundEnabled, volume }] = useAtom(soundAtom);
  const [playSuccessSound] = UseSound.useSound('/sounds/success.mp3', { volume });
  const [playErrorSound] = UseSound.useSound('/sounds/error.mp3', { volume });

  const debounceTimeout = useRef<number | undefined>(undefined);

  // Stabilize frequently changing dependencies with refs
  const playSuccessSoundRef = useRef(playSuccessSound);
  const playErrorSoundRef = useRef(playErrorSound);
  const workerFeedbackRef = useRef(workerFeedback);
  const soundEnabledRef = useRef(soundEnabled);

  // Keep refs up to date
  playSuccessSoundRef.current = playSuccessSound;
  playErrorSoundRef.current = playErrorSound;
  workerFeedbackRef.current = workerFeedback;
  soundEnabledRef.current = soundEnabled;

  const workerFeedbackDebounce = useCallback((actionId: ProGloveWorkerFeedbackActionId) => {
    window.clearTimeout(debounceTimeout.current);
    debounceTimeout.current = window.setTimeout(() => {
      workerFeedbackRef.current(actionId);
    }, 100);
  }, []);

  /** use a custom snackbar key via options: { key: 'myKey' } to prevent duplicates */
  const enqueueFeedback = useCallback(
    (message: string, options?: OptionsWithExtraProps<VariantType>, mute?: boolean): void => {
      const feedbackOptions = options ?? { variant: 'default' };
      const { variant } = feedbackOptions;
      let { key } = feedbackOptions;
      if (key !== undefined) {
        closeSnackbar(key);
      }

      if (!skipProGlove && variant !== undefined) {
        const workerFeedbackActionId = variantWorkerFeedbackMap[variant];

        if (workerFeedbackActionId !== null) {
          workerFeedbackDebounce(workerFeedbackActionId);
        }
      }

      if (!mute && !skipSound && soundEnabledRef.current) {
        switch (variant) {
          case 'success':
            playSuccessSoundRef.current();
            break;
          case 'error':
          case 'warning':
            playErrorSoundRef.current();
            break;
          default:
            break;
        }
      }

      key = enqueueSnackbar(message, {
        ...feedbackOptions,
        SnackbarProps: {
          ...feedbackOptions.SnackbarProps,
          onClick: (e) => {
            closeSnackbar(key);
            const originalClick = feedbackOptions.SnackbarProps?.onClick;
            if (originalClick !== undefined) {
              originalClick(e);
            }
          },
        },
      });
    },
    [enqueueSnackbar, closeSnackbar, skipProGlove, skipSound, workerFeedbackDebounce],
  );

  return { enqueueFeedback, playSuccessSound, playErrorSound, soundEnabled };
}
